import * as React from 'react';
import { applyControlledFieldValue } from '@/lib/controlled-field-change';
import { cn } from '@/lib/utils';
import { VoiceInputButton } from '@/components/voice/VoiceInputButton';

export interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  voiceInput?: boolean;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type = 'text', voiceInput = true, value, onChange, disabled, ...props }, ref) => {
    const innerRef = React.useRef<HTMLInputElement | null>(null);
    const mergedRef = React.useCallback(
      (node: HTMLInputElement | null) => {
        innerRef.current = node;
        if (typeof ref === 'function') ref(node);
        else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
      },
      [ref]
    );

    const hideVoice =
      !voiceInput ||
      disabled ||
      type === 'password' ||
      type === 'number' ||
      type === 'hidden' ||
      type === 'file' ||
      type === 'checkbox' ||
      type === 'radio' ||
      type === 'range' ||
      type === 'color' ||
      type === 'submit' ||
      type === 'button';

    const v = value as string | number | readonly string[] | undefined;
    const str = typeof v === 'string' ? v : v === undefined || v === null ? '' : String(v);

    const pushChange = (next: string) => {
      const el = innerRef.current;
      if (el) {
        applyControlledFieldValue(el, next, onChange as React.ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>);
        return;
      }
      if (value === undefined) return;
      onChange?.({
        target: { value: next } as HTMLInputElement,
        currentTarget: { value: next } as HTMLInputElement,
      } as React.ChangeEvent<HTMLInputElement>);
    };

    return (
      <div className="relative flex w-full min-w-0 items-center">
        <input
          type={type}
          value={v}
          onChange={onChange}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full rounded-md border border-border bg-card px-3 py-1 text-sm transition-colors',
            'placeholder:text-muted',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'dark:text-foreground dark:focus-visible:ring-offset-background',
            !hideVoice && 'pr-10',
            className
          )}
          ref={mergedRef}
          {...props}
        />
        {!hideVoice && (
          <VoiceInputButton
            inputRef={innerRef}
            value={str}
            onValueChange={pushChange}
            disabled={disabled}
            placement="inline"
          />
        )}
      </div>
    );
  }
);
Input.displayName = 'Input';

export { Input };
